/**
 * Avis et idées sur la feuille de brouillon (migration 0696) : une idée ou un problème (icône du haut de la
 * feuille), ou un avis de 1 à 5 étoiles avec une suggestion (fin de la feuille). Écrit en direct sous RLS (le
 * compte connecté, jamais une session anonyme). Les règles d'envoi sont pures et testées.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export type ScratchFeedbackKind = 'idea' | 'problem' | 'review';
export type ScratchFeedbackContext = 'exercise' | 'tutorial' | 'demo' | 'live';

export const FEEDBACK_MESSAGE_MAX = 1000;
export const FEEDBACK_LINES_MAX = 40;

export interface ScratchFeedbackInput {
  kind: ScratchFeedbackKind;
  stars?: number | null;
  message?: string;
  context: ScratchFeedbackContext;
  exerciseId?: string | null;
  questionId?: string | null;
  attemptId?: string | null;
  /** lignes écrites sur la feuille (texte), pour comprendre l'avis */
  lines?: string[];
}

/** Ce qui manque pour envoyer (null : prêt). */
export function feedbackIssue(
  input: Pick<ScratchFeedbackInput, 'kind' | 'stars' | 'message'>,
): string | null {
  const msg = (input.message ?? '').trim();
  if (msg.length > FEEDBACK_MESSAGE_MAX) return `${FEEDBACK_MESSAGE_MAX} caractères au plus.`;
  if (input.kind === 'review') {
    const s = input.stars ?? 0;
    return s >= 1 && s <= 5 ? null : 'Choisis de 1 à 5 étoiles.';
  }
  return msg.length >= 3 ? null : 'Écris quelques mots.';
}

/** La ligne envoyée en base. */
export function feedbackRow(input: ScratchFeedbackInput) {
  const msg = (input.message ?? '').trim();
  return {
    kind: input.kind,
    stars: input.kind === 'review' ? (input.stars ?? null) : null,
    message: msg ? msg.slice(0, FEEDBACK_MESSAGE_MAX) : null,
    context: input.context,
    exercise_id: input.exerciseId ?? null,
    question_block_id: input.questionId ?? null,
    attempt_id: input.attemptId ?? null,
    lines: (input.lines ?? []).slice(-FEEDBACK_LINES_MAX).map((l) => l.slice(0, 400)),
  };
}

/** Message lisible d'une erreur d'envoi. */
export function feedbackErrorMessage(error: { message?: string; code?: string } | null): string {
  const m = error?.message ?? '';
  if (/feedback_rate_limited/.test(m))
    return 'Tu as déjà envoyé beaucoup de messages aujourd’hui : réessaie demain.';
  if (error?.code === '42501' || /row-level security|JWT|not authenticated/i.test(m))
    return 'Connecte-toi pour envoyer ton message.';
  return 'Ton message n’est pas parti. Réessaie dans un instant.';
}

export async function sendScratchFeedback(input: ScratchFeedbackInput): Promise<void> {
  const issue = feedbackIssue(input);
  if (issue) throw new Error(issue);
  const { data: auth } = await supabase.auth.getSession();
  if (!auth.session) throw new Error('Connecte-toi pour envoyer ton message.');
  const db = supabase as unknown as SupabaseClient;
  const { error } = await db.from('scratch_feedback').insert(feedbackRow(input));
  if (error) throw new Error(feedbackErrorMessage(error));
}

/** Avis en étoiles : la première fois une ligne est créée (son id est renvoyé), ensuite elle est corrigée. */
export async function saveScratchReview(
  input: Omit<ScratchFeedbackInput, 'kind'> & { stars: number },
  id: string | null,
): Promise<string> {
  const db = supabase as unknown as SupabaseClient;
  if (id) {
    const { error } = await db.rpc('update_my_scratch_review', {
      p_id: id,
      p_stars: input.stars,
      p_message: (input.message ?? '').trim() || null,
    });
    if (error) throw new Error(feedbackErrorMessage(error));
    return id;
  }
  const row = { ...input, kind: 'review' as const };
  const issue = feedbackIssue(row);
  if (issue) throw new Error(issue);
  const { data: auth } = await supabase.auth.getSession();
  if (!auth.session) throw new Error('Connecte-toi pour envoyer ton message.');
  const { data, error } = await db
    .from('scratch_feedback')
    .insert(feedbackRow(row))
    .select('id')
    .single();
  if (error) throw new Error(feedbackErrorMessage(error));
  return (data as { id: string }).id;
}

/** Délai avant l'envoi automatique de l'avis : le temps de changer d'étoile ou de finir sa phrase. */
export const REVIEW_SAVE_DELAY_MS = 4000;

// ── avis déjà donné sur une question (sur cet appareil) ─────────────────────
const REVIEW_KEY = 'jr_scratch_review:';

export function hasReviewed(key: string): boolean {
  try {
    return localStorage.getItem(REVIEW_KEY + key) === '1';
  } catch {
    return false;
  }
}
export function markReviewed(key: string): void {
  try {
    localStorage.setItem(REVIEW_KEY + key, '1');
  } catch {
    /* stockage indisponible */
  }
}

// ── admin ──────────────────────────────────────────────────────────────────
export interface ScratchFeedbackRow {
  id: string;
  user_id: string;
  kind: ScratchFeedbackKind;
  stars: number | null;
  message: string | null;
  context: ScratchFeedbackContext;
  exercise_id: string | null;
  question_block_id: string | null;
  attempt_id: string | null;
  lines: string[];
  status: 'open' | 'done';
  created_at: string;
  author?: { display_name: string | null } | null;
  exercise?: { title: string | null } | null;
}

/** Moyenne des étoiles (une décimale) et nombre d'avis. */
export function reviewStats(rows: Pick<ScratchFeedbackRow, 'kind' | 'stars'>[]): {
  avg: number | null;
  count: number;
} {
  const stars = rows.filter((r) => r.kind === 'review' && r.stars).map((r) => r.stars!);
  if (!stars.length) return { avg: null, count: 0 };
  return {
    avg: Math.round((stars.reduce((a, b) => a + b, 0) / stars.length) * 10) / 10,
    count: stars.length,
  };
}

export type FeedbackStatusFilter = 'open' | 'done';

const ADMIN_SELECT =
  'id, user_id, kind, stars, message, context, exercise_id, question_block_id, attempt_id, lines, status, created_at, author:profiles!scratch_feedback_user_id_fkey(display_name), exercise:exercises!scratch_feedback_exercise_id_fkey(title)';

/** Admin : les avis et idées (200 derniers d'un statut). */
export function useScratchFeedbackAdmin(status: FeedbackStatusFilter, enabled: boolean) {
  return useQuery({
    queryKey: ['scratch-feedback-admin', status],
    enabled,
    queryFn: async (): Promise<ScratchFeedbackRow[]> => {
      const db = supabase as unknown as SupabaseClient;
      const { data, error } = await db
        .from('scratch_feedback')
        .select(ADMIN_SELECT)
        .eq('status', status)
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw new Error('Impossible de charger les avis.');
      return (data ?? []) as unknown as ScratchFeedbackRow[];
    },
  });
}

/** Admin : marquer lu / à relire. */
export function useSetScratchFeedbackStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: FeedbackStatusFilter }) => {
      const db = supabase as unknown as SupabaseClient;
      const { error } = await db.from('scratch_feedback').update({ status }).eq('id', id);
      if (error) throw new Error('La mise à jour a échoué.');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['scratch-feedback-admin'] }),
  });
}
